import { BlueprintType } from '@constants'
import { IBlueprint } from '@root/query'
import { calculateDebetCreditDifferenceFromBlueprint, calculateEntriesTotal } from '@root/utils'
import React, { useCallback } from 'react'
import { useFormContext, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form'
import { type EuSaleWarning } from './euB2bVatDetails'
import { getBlueprintAccountIds } from './relationMapperUtils'

export const useBlueprintAccountEffect = (forAccountId?: number): number => {
  const { control } = useFormContext()
  const blueprint = useWatch({ control, name: 'blueprint' })
  const blueprintType = useWatch({ control, name: 'blueprint_type' })

  const totalExp =
    blueprint?.expense_entries
      ?.filter(({ account_id }) => !account_id || account_id !== forAccountId)
      .reduce((acc, { amount }) => acc + (amount ? Math.round(amount * 100) : 0), 0) || 0

  const totalDeb =
    blueprint?.debet_entries
      ?.filter(({ account_id }) => !account_id || account_id !== forAccountId)
      .reduce((acc, { amount }) => acc + (amount ? Math.round(amount * 100) : 0), 0) || 0

  const totalCre =
    blueprint?.credit_entries
      ?.filter(({ account_id }) => !account_id || account_id !== forAccountId)
      .reduce((acc, { amount }) => acc + (amount ? Math.round(amount * 100) : 0), 0) || 0

  if (blueprintType === BlueprintType.SALES) {
    return (totalCre - totalExp) / 100
  }

  if (blueprintType === BlueprintType.PURCHASE) {
    return -totalDeb / 100
  }

  return (totalCre - totalDeb) / 100
}

export const useBlueprintDebetCreditDifference = (): number | null => {
  const { control } = useFormContext()
  const blueprint: IBlueprint = useWatch({ control, name: 'blueprint' })
  const blueprintType: BlueprintType = useWatch({ control, name: 'blueprint_type' })

  if (
    blueprintType !== BlueprintType.MANUAL ||
    blueprint?.credit_entries?.length === 0 ||
    blueprint?.debet_entries?.length === 0
  ) {
    return null
  }

  return Math.round(calculateDebetCreditDifferenceFromBlueprint(blueprint) * 100) / 100
}

export const amountsDifferInCents = (calculatedAmount: number, expectedAmount: number): boolean =>
  Math.round(calculatedAmount * 100) !== Math.round(expectedAmount * 100)

/** The import amount is the effect on a payment account. Without that account it is not an expected balance. */
export const expectedAmountForPaymentAccount = (
  importData?: { amount?: number | null; payment_account_id?: number | null } | null
): number | undefined => {
  if (importData?.payment_account_id == null || importData.amount == null) return undefined
  return importData.amount
}

export type ConfirmPostingWarningReason =
  | 'bank'
  | 'debetCredit'
  | 'manualBankConnection'
  | 'euB2bMissingContact'
  | 'euB2bMissingVatId'
  | 'euPrivatePerson'

const postingWarningI18nKeys = {
  bank: {
    title: 'document.acceptAmountDiffersConfirmTitle',
    description: 'document.acceptAmountDiffersConfirmDescription'
  },
  debetCredit: {
    title: 'document.acceptDebetCreditMismatchConfirmTitle',
    description: 'document.acceptDebetCreditMismatchConfirmDescription'
  },
  manualBankConnection: {
    title: 'document.acceptManualBankConnectionConfirmTitle',
    description: 'document.acceptManualBankConnectionConfirmDescription'
  },
  euB2bMissingContact: {
    title: 'document.acceptEuB2bMissingContactConfirmTitle',
    description: 'document.acceptEuB2bMissingContactConfirmDescription'
  },
  euB2bMissingVatId: {
    title: 'document.acceptEuB2bMissingVatIdConfirmTitle',
    description: 'document.acceptEuB2bMissingVatIdConfirmDescription'
  },
  euPrivatePerson: {
    title: 'document.euPrivatePersonTitle',
    description: 'document.acceptEuPrivatePersonConfirmDescription'
  }
} satisfies Record<ConfirmPostingWarningReason, { title: string; description: string }>

export const postingWarningTitleKey = (reason: ConfirmPostingWarningReason): string =>
  postingWarningI18nKeys[reason].title

export const postingWarningDescriptionKey = (reason: ConfirmPostingWarningReason): string =>
  postingWarningI18nKeys[reason].description

const hasConnectedBankAccountInBlueprint = (
  blueprint: IBlueprint | undefined,
  connectedBankAccountIds: Set<number>
): boolean => {
  if (connectedBankAccountIds.size === 0) return false
  const accountIds = getBlueprintAccountIds(blueprint)
  return accountIds.some(accountId => connectedBankAccountIds.has(accountId))
}

/** Returns the reason to show the confirm prompt when posting with warnings, or null if none. */
export const getConfirmPostingWarning = (params: {
  expectedAmount: number | undefined
  calculatedAmount: number
  debetCreditDiff: number | null
  blueprint?: IBlueprint
  isManualCreation?: boolean
  connectedBankAccountIds?: Set<number>
  euSaleWarning?: EuSaleWarning
}): { reason: ConfirmPostingWarningReason } | null => {
  const {
    expectedAmount,
    calculatedAmount,
    debetCreditDiff,
    blueprint,
    isManualCreation = false,
    connectedBankAccountIds = new Set(),
    euSaleWarning = null
  } = params
  if (expectedAmount != null && amountsDifferInCents(calculatedAmount, expectedAmount)) {
    return { reason: 'bank' }
  }
  if (expectedAmount == null && debetCreditDiff != null && debetCreditDiff !== 0) {
    return { reason: 'debetCredit' }
  }
  if (
    expectedAmount == null &&
    isManualCreation &&
    hasConnectedBankAccountInBlueprint(blueprint, connectedBankAccountIds)
  ) {
    return { reason: 'manualBankConnection' }
  }
  if (euSaleWarning === 'privatePerson') {
    return { reason: 'euPrivatePerson' }
  }
  if (euSaleWarning === 'missingContact') {
    return { reason: 'euB2bMissingContact' }
  }
  if (euSaleWarning === 'missingVatId') {
    return { reason: 'euB2bMissingVatId' }
  }
  return null
}

export const BlueprintPaymentAmountSync: React.FC<{
  /**
   * Syncs useBlueprintAccountEffect into a ref on each render for use in submit handlers.
   */
  paymentAccountId?: number
  calculatedAmountRef: React.MutableRefObject<number>
}> = ({ paymentAccountId, calculatedAmountRef }) => {
  calculatedAmountRef.current = useBlueprintAccountEffect(paymentAccountId)
  return null
}

export const BlueprintDebetCreditDiffSync: React.FC<{
  /**
   * Syncs useBlueprintDebetCreditDifference into a ref on each render for use in submit handlers.
   */
  debetCreditDiffRef: React.MutableRefObject<number | null>
}> = ({ debetCreditDiffRef }) => {
  debetCreditDiffRef.current = useBlueprintDebetCreditDifference()
  return null
}

export const useEntryUpdater = setValue => {
  /**
   * Has the update and delete logic separated as
   * if using the delete logic for update
   * it would not update the amounts correctly when the side being changed
   * has only one entry and the opposite has multiple entries.
   */

  const updateFirstEntryAmount = (basePath, amount) => {
    setValue(`${basePath}.0.amount`, Math.round(amount * 100) / 100)
  }

  const updateEntryBalance = (changedEntryType: string, blueprint: IBlueprint) => {
    const targetEntryType =
      changedEntryType === 'blueprint.debet_entries'
        ? 'blueprint.credit_entries'
        : 'blueprint.debet_entries'

    const sourceEntries =
      changedEntryType === 'blueprint.debet_entries'
        ? blueprint?.debet_entries
        : blueprint?.credit_entries

    const targetEntries =
      changedEntryType === 'blueprint.debet_entries'
        ? blueprint?.credit_entries
        : blueprint?.debet_entries

    if (targetEntries?.length === 1) {
      const totalAmount = calculateEntriesTotal(sourceEntries)
      updateFirstEntryAmount(targetEntryType, totalAmount)
    }
  }

  const updateEntryAmountsWhenAddingOrChangingEntry = (
    baseNameThatChanged: string,
    blueprint: IBlueprint,
    blueprintType: BlueprintType
  ) => {
    if (blueprintType !== BlueprintType.MANUAL) return

    updateEntryBalance(baseNameThatChanged, blueprint)
  }

  const updateEntryAmountsWhenEntryIsRemoved = (
    baseNameThatChanged: string,
    blueprint: IBlueprint,
    blueprintType: BlueprintType
  ) => {
    if (blueprintType !== BlueprintType.MANUAL) return

    // Cases when removing entry leaves only one etry per side thus
    // need to use the total amount of the side that had the entry removed from.
    if (
      baseNameThatChanged === 'blueprint.credit_entries' &&
      blueprint?.credit_entries?.length === 1 &&
      blueprint?.debet_entries?.length === 1
    ) {
      updateEntryBalance(baseNameThatChanged, blueprint)
      return
    }
    if (
      baseNameThatChanged === 'blueprint.debet_entries' &&
      blueprint?.debet_entries?.length === 1 &&
      blueprint?.credit_entries?.length === 1
    ) {
      updateEntryBalance(baseNameThatChanged, blueprint)
      return
    }

    // Cases where removing an entry leaves only one
    // entry on the side that had the entry removed from.
    // In this case we need to set the amount of the remaining
    // entry to the total amount of the other side.
    if (
      baseNameThatChanged === 'blueprint.credit_entries' &&
      blueprint?.credit_entries?.length === 1
    ) {
      const debetTotalAmount = calculateEntriesTotal(blueprint?.debet_entries)
      updateFirstEntryAmount(baseNameThatChanged, debetTotalAmount)
      return
    }
    if (
      baseNameThatChanged === 'blueprint.debet_entries' &&
      blueprint?.debet_entries?.length === 1
    ) {
      const creditTotalAmount = calculateEntriesTotal(blueprint?.credit_entries)
      updateFirstEntryAmount(baseNameThatChanged, creditTotalAmount)
      return
    }

    // Case where removing an entry leaves more than one
    // entry on the side that had the entry removed from.
    updateEntryBalance(baseNameThatChanged, blueprint)
  }

  return { updateEntryAmountsWhenAddingOrChangingEntry, updateEntryAmountsWhenEntryIsRemoved }
}

export const useUpdateEntryAmounts = <T extends { blueprint?: IBlueprint }>(
  getValues: UseFormGetValues<T>,
  setValue: UseFormSetValue<T>
) => {
  // The getValues and setValues have the correct types
  // to work with forms that have blueprint
  // but they are unable to determine the generic type thus
  // need to cast the names to 'any' type.

  const updateEntryAmounts = useCallback(
    (oldBluePrintType: BlueprintType) => {
      const debetEntries = getValues('blueprint.debet_entries' as any)
      const creditEntries = getValues('blueprint.credit_entries' as any)

      if (
        oldBluePrintType === BlueprintType.PURCHASE &&
        (creditEntries?.length <= 1 || creditEntries === undefined)
      ) {
        const totalDebet = calculateEntriesTotal(debetEntries)
        setValue('blueprint.credit_entries.0.amount' as any, totalDebet as any)
      } else if (
        oldBluePrintType === BlueprintType.SALES &&
        (debetEntries?.length <= 1 || debetEntries === undefined)
      ) {
        const totalCredit = calculateEntriesTotal(creditEntries)
        setValue('blueprint.debet_entries.0.amount' as any, totalCredit as any)
      }
    },
    [getValues, setValue]
  )

  return updateEntryAmounts
}
